import { createFileRoute, Link } from "@tanstack/react-router";
import { SessionAwareLink } from "@/components/session-aware-link";
import { SiteFooter } from "@/components/site-footer";
import { OFFICIAL_APP_URL } from "@/lib/app-url";
import { formatDateBR } from "@/lib/datetime";

export const Route = createFileRoute("/privacidade")({
  head: () => ({
    meta: [
      { title: "Política de Privacidade · Beit Tefilah Rechovot" },
      {
        name: "description",
        content:
          "Política de Privacidade da área de membros da Beit Tefilah Rechovot: quais dados coletamos, como usamos e seus direitos.",
      },
      { property: "og:title", content: "Política de Privacidade · Beit Tefilah Rechovot" },
      {
        property: "og:description",
        content:
          "Como tratamos seus dados pessoais na plataforma de ensinos da Beit Tefilah Rechovot.",
      },
      { property: "og:url", content: `${OFFICIAL_APP_URL}/privacidade` },
    ],
    links: [{ rel: "canonical", href: `${OFFICIAL_APP_URL}/privacidade` }],
  }),
  component: PrivacidadePage,
});

function PrivacidadePage() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border/60 bg-background/95">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-serif text-lg font-semibold text-primary">
            Beit Tefilah Rechovot
          </Link>
          <div className="flex gap-4 text-sm">
            <Link to="/termos" className="text-muted-foreground hover:text-primary">
              Termos
            </Link>
            <SessionAwareLink className="text-muted-foreground hover:text-primary" />
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-12">
        <h1 className="font-serif text-4xl font-semibold text-primary">Política de Privacidade</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Última atualização: {formatDateBR(new Date())}
        </p>

        <div className="prose prose-slate mt-8 max-w-none space-y-6 text-foreground/90">
          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">1. Quem somos</h2>
            <p>
              Esta plataforma é mantida pela congregação <strong>Beit Tefilah Rechovot</strong> e
              destina-se exclusivamente aos seus membros para acesso aos ensinos ministrados. Para
              dúvidas sobre privacidade, entre em contato pelo e-mail{" "}
              <a href="mailto:contato@rechovot.org.br" className="text-primary underline">
                contato@rechovot.org.br
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">
              2. Dados que coletamos
            </h2>
            <ul className="list-inside list-disc space-y-1">
              <li>
                <strong>Cadastro:</strong> nome completo, e-mail, CPF e senha.
              </li>
              <li>
                <strong>Perfil:</strong> foto opcional e preferências de aparência.
              </li>
              <li>
                <strong>Uso da plataforma:</strong> favoritos, progresso de vídeos assistidos e
                comentários publicados.
              </li>
              <li>
                <strong>Registros técnicos:</strong> logs de segurança (login, aprovações, ações
                administrativas).
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">
              3. Como usamos seus dados
            </h2>
            <ul className="list-inside list-disc space-y-1">
              <li>Autenticar seu acesso e proteger sua conta.</li>
              <li>Personalizar sua experiência (continuar assistindo, favoritos, tema).</li>
              <li>Aprovar manualmente novos cadastros e comunicar liberação por e-mail.</li>
              <li>Notificar o suporte técnico em caso de falhas.</li>
            </ul>
            <p>
              Não vendemos, alugamos ou compartilhamos seus dados com terceiros para fins
              comerciais.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">4. Vídeos do YouTube</h2>
            <p>
              Os ensinos são reproduzidos por meio do player oficial do YouTube incorporado à
              plataforma. Ao assistir, o YouTube pode coletar dados conforme sua própria política de
              privacidade.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">
              5. Armazenamento e segurança
            </h2>
            <p>
              Utilizamos infraestrutura em nuvem com criptografia em trânsito (HTTPS) e em repouso.
              Senhas são armazenadas de forma cifrada. O acesso administrativo é restrito e
              auditado.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">
              6. Seus direitos (LGPD)
            </h2>
            <p>
              Você pode a qualquer momento solicitar acesso, correção, portabilidade ou exclusão dos
              seus dados, bem como revogar consentimentos, escrevendo para{" "}
              <a href="mailto:contato@rechovot.org.br" className="text-primary underline">
                contato@rechovot.org.br
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">7. Cookies</h2>
            <p>
              Usamos cookies estritamente necessários para manter sua sessão autenticada e lembrar
              suas preferências. Não utilizamos cookies de publicidade.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-primary">8. Alterações</h2>
            <p>
              Esta política pode ser atualizada. Publicaremos a nova versão nesta página com a data
              de revisão.
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
